import React, { useState, useEffect, useCallback } from 'react';
import { ref, push, get, update, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db, storage } from './firebase-config';
import Login from './Login';
import NewsletterSubscribers from './NewsletterSubscribers';
import ApplicationsList from './ApplicationsList';
import './TeamDashboard.css';

function TeamDashboard() {
  const [artistName, setArtistName] = useState('');
  const [artistLink, setArtistLink] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [spotifyListeners, setSpotifyListeners] = useState('');
  const [instagramFollowers, setInstagramFollowers] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [artists, setArtists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [activeTab, setActiveTab] = useState('artists'); // 'artists', 'subscribers', or 'applications'

  // Laden der vorhandenen Artists als memoized Funktion
  const loadArtists = useCallback(async () => {
    if (!isLoggedIn) return;
    
    setIsLoading(true);
    try {
      const artistsRef = ref(db, 'artists');
      const snapshot = await get(artistsRef);
      
      if (snapshot.exists()) {
        const artistsData = snapshot.val();
        const artistsList = Object.keys(artistsData).map(key => ({
          id: key,
          ...artistsData[key]
        }));
        setArtists(artistsList);
      } else {
        setArtists([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Artists:', err);
      setError('Fehler beim Laden der Artists.');
    } finally {
      setIsLoading(false);
    }
  }, [isLoggedIn]);

  // Nur einmal laden, wenn sich der Login-Status ändert oder ein Refresh angefordert wird
  useEffect(() => {
    if (isLoggedIn) {
      loadArtists();
    }
    // Reset shouldRefresh nach dem Laden
    if (shouldRefresh) {
      setShouldRefresh(false);
    }
  }, [isLoggedIn, shouldRefresh, loadArtists]);

  // Funktion zum Bereinigen von Dateinamen
  const cleanFileName = (fileName) => {
    // Ersetze Leerzeichen und Sonderzeichen durch Unterstriche
    const cleanName = fileName.replace(/[^a-zA-Z0-9.]/g, '_');
    // Füge einen Zeitstempel hinzu, um Eindeutigkeit zu gewährleisten
    const timestamp = new Date().getTime();
    return `${timestamp}_${cleanName}`;
  };

  // Konvertiert ein Bild in eine Data URL
  const getImageAsDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Hilfsfunktion zur Größenreduzierung von Bildern
  const resizeImage = async (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
    try {
      const dataUrl = await getImageAsDataUrl(file);
      
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          let width = img.width;
          let height = img.height;
          
          // Berechne neue Dimensionen, wenn das Bild zu groß ist
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Konvertiere Canvas zu Blob
          canvas.toBlob((blob) => {
            if (blob) {
              // Erstelle eine neue Datei aus dem Blob
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(resizedFile);
            } else {
              resolve(file); // Fallback zum Original
            }
          }, file.type, quality);
        };
        img.src = dataUrl;
      });
    } catch (error) {
      console.error('Fehler beim Verkleinern des Bildes:', error);
      return file; // Fallback zum Original
    }
  };

  const handleImageUpload = async (file) => {
    if (!isLoggedIn) {
      setError('Bitte melde dich an, um Bilder hochzuladen.');
      return null;
    }
    
    setIsUploading(true);
    setError('');
    
    // Prüfen, ob die Datei zu groß ist
    const MAX_FILE_SIZE_MB = 5;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Die Datei ist zu groß (max. ${MAX_FILE_SIZE_MB}MB). Bitte wähle eine kleinere Datei.`);
      setIsUploading(false);
      return null;
    }
    
    try {
      // Bild verkleinern, um Upload-Probleme zu vermeiden
      const resizedFile = await resizeImage(file, 1200, 1200, 0.85);
      
      // Direkt die Data URL erstellen (Fallback-Methode)
      console.log('Verwende Data URL-Methode für Bildupload...');
      const dataUrl = await getImageAsDataUrl(resizedFile);
      console.log('Bild erfolgreich als Data URL gespeichert');
      
      setIsUploading(false);
      return dataUrl;
    } catch (err) {
      console.error('Fehler beim Verarbeiten des Bildes:', err);
      setError('Fehler beim Verarbeiten des Bildes: ' + (err.message || 'Unbekannter Fehler'));
      setIsUploading(false);
      return null;
    }
  };

  const resetForm = () => {
    setArtistName('');
    setArtistLink('');
    setImageFile(null);
    setSpotifyListeners('');
    setInstagramFollowers('');
    setEditingArtist(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isLoggedIn) {
      setError('Bitte melde dich an, um Daten zu speichern.');
      return;
    }
    
    setError('');
    setSuccess('');
    
    if (!artistName || !artistLink) {
      setError('Bitte Name und Link ausfüllen.');
      return;
    }

    let imageUrl = '';
    if (imageFile) {
      setSuccess('Bild wird verarbeitet...');
      imageUrl = await handleImageUpload(imageFile);
      
      if (!imageUrl) {
        setError('Bild konnte nicht verarbeitet werden. Bitte versuche es erneut.');
        return;
      }
      
      setSuccess('Bild wurde erfolgreich gespeichert');
    } else if (editingArtist && editingArtist.image) {
      // Behalte das bestehende Bild bei Bearbeitung
      imageUrl = editingArtist.image;
      console.log('Bestehendes Bild beibehalten:', imageUrl);
    } else {
      setError('Bitte ein Bild hochladen.');
      return;
    }

    // Speichere die Daten
    setSuccess('Speichere Artist-Daten...');
    
    const artistData = {
      name: artistName,
      link: artistLink,
      image: imageUrl,
      spotifyListeners: spotifyListeners || '',
      instagramFollowers: instagramFollowers || '',
      updatedAt: new Date().toISOString()
    };

    try {
      if (editingArtist) {
        // Aktualisieren eines vorhandenen Artists
        await update(ref(db, `artists/${editingArtist.id}`), artistData);
        setSuccess('Artist erfolgreich aktualisiert!');
      } else {
        // Hinzufügen eines neuen Artists
        await push(ref(db, 'artists'), artistData);
        setSuccess('Artist erfolgreich hinzugefügt!');
      }
      
      resetForm();
      // Statt direktem Aufruf von loadArtists, setzen wir shouldRefresh
      setShouldRefresh(true);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Fehler beim Speichern in der Datenbank: ' + (err.message || 'Unbekannter Fehler'));
    }
  };

  const handleEdit = (artist) => {
    setArtistName(artist.name);
    setArtistLink(artist.link);
    setSpotifyListeners(artist.spotifyListeners || '');
    setInstagramFollowers(artist.instagramFollowers || '');
    setImageFile(null);
    setEditingArtist(artist);
    
    // Sanfteres Scrollen zum Formular
    setTimeout(() => {
      const formContainer = document.querySelector('.team-dashboard-form-container');
      if (formContainer) {
        formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDelete = async (artist) => {
    if (!isLoggedIn) return;
    
    if (window.confirm(`Möchtest du den Artist "${artist.name}" wirklich löschen?`)) {
      try {
        // Löschen des Artists aus der Datenbank
        await remove(ref(db, `artists/${artist.id}`));
        
        // Wenn das Bild in Firebase Storage gespeichert ist, versuche es zu löschen
        if (artist.image && artist.image.includes('firebase')) {
          try {
            // Extrahiere den Pfad aus der URL
            const imageUrl = new URL(artist.image);
            const pathMatch = imageUrl.pathname.match(/o\/([^?]+)/);
            if (pathMatch && pathMatch[1]) {
              const imagePath = decodeURIComponent(pathMatch[1]);
              const imageRef = storageRef(storage, imagePath);
              await deleteObject(imageRef);
            }
          } catch (imgErr) {
            console.error('Fehler beim Löschen des Bildes:', imgErr);
            // Wir setzen fort, auch wenn das Bild nicht gelöscht werden konnte
          }
        }
        
        setSuccess(`Artist "${artist.name}" wurde erfolgreich gelöscht.`);
        // Statt direktem Aufruf von loadArtists, setzen wir shouldRefresh
        setShouldRefresh(true);
      } catch (err) {
        console.error('Fehler beim Löschen:', err);
        setError('Fehler beim Löschen des Artists.');
      }
    }
  };

  const handleLoginStatusChange = (status) => {
    setIsLoggedIn(status);
    if (!status) {
      // Zurücksetzen der Formularfelder, wenn der Benutzer sich abmeldet
      resetForm();
      setError('');
      setSuccess('');
      setArtists([]);
    }
    // Wir brauchen keinen loadArtists-Aufruf hier, da useEffect darauf reagiert
  };

  const handleCancelEdit = () => {
    resetForm();
    setError('');
    setSuccess('');
  };

  // Formatierung von Zahlen mit Tausendertrennzeichen
  const formatNumber = (num) => {
    if (!num) return '';
    return new Intl.NumberFormat('de-DE').format(num);
  };

  return (
    <div className="team-dashboard-container">
      <h1>Admin Dashboard</h1>
      
      <Login onLoginStatusChange={handleLoginStatusChange} />
      
      {isLoggedIn && (
        <>
          <div className="team-dashboard-tabs">
            <button 
              className={`team-dashboard-tab ${activeTab === 'artists' ? 'active' : ''}`}
              onClick={() => setActiveTab('artists')}
            >
              Manage Artists
            </button>
            <button 
              className={`team-dashboard-tab ${activeTab === 'applications' ? 'active' : ''}`}
              onClick={() => setActiveTab('applications')}
            >
              Applications
            </button>
            <button 
              className={`team-dashboard-tab ${activeTab === 'subscribers' ? 'active' : ''}`}
              onClick={() => setActiveTab('subscribers')}
            >
              Newsletter Subscribers
            </button>
          </div>
          
          {activeTab === 'artists' ? (
            <div className="team-dashboard-content">
              <div className="team-dashboard-form-container">
                <h2>{editingArtist ? 'Edit Artist' : 'Add New Artist'}</h2>
                <form className="team-dashboard-form" onSubmit={handleSubmit}>
                  <label>
                    Artist Name
                    <input
                      type="text"
                      value={artistName}
                      onChange={(e) => setArtistName(e.target.value)}
                      placeholder="Artist Name"
                      required
                    />
                  </label>
                  
                  <label>
                    Artist Link (Spotify, Website, etc.)
                    <input
                      type="url"
                      value={artistLink}
                      onChange={(e) => setArtistLink(e.target.value)}
                      placeholder="https://..."
                      required
                    />
                  </label>
                  
                  <label>
                    Spotify Monthly Listeners
                    <input
                      type="number"
                      value={spotifyListeners}
                      onChange={(e) => setSpotifyListeners(e.target.value)}
                      placeholder="Number of listeners (optional)"
                    />
                  </label>
                  
                  <label>
                    Instagram Followers
                    <input
                      type="number"
                      value={instagramFollowers}
                      onChange={(e) => setInstagramFollowers(e.target.value)}
                      placeholder="Number of followers (optional)"
                    />
                  </label>
                  
                  <label>
                    Artist Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => setImageFile(e.target.files[0])}
                    />
                  </label>
                  
                  {editingArtist && editingArtist.image && (
                    <div className="team-dashboard-current-image">
                      <span>Current Image:</span>
                      <img 
                        src={editingArtist.image} 
                        alt={editingArtist.name} 
                        style={{ maxWidth: '100%', maxHeight: '150px' }}
                      />
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : editingArtist ? 'Update Artist' : 'Add Artist'}
                  </button>
                  
                  {editingArtist && (
                    <button 
                      type="button" 
                      onClick={handleCancelEdit}
                      className="team-dashboard-cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                </form>
                
                {isUploading && <p className="team-dashboard-uploading">Uploading image...</p>}
                {error && <p className="team-dashboard-error">{error}</p>}
                {success && <p className="team-dashboard-success">{success}</p>}
              </div>
              
              <div className="team-dashboard-artists-container">
                <h2>Existing Artists</h2>
                {isLoading ? (
                  <div className="team-dashboard-loading">Loading artists...</div>
                ) : artists.length > 0 ? (
                  <div className="team-dashboard-artists-grid">
                    {artists.map(artist => (
                      <div key={artist.id} className="team-dashboard-artist-card">
                        <div className="team-dashboard-artist-image">
                          <img src={artist.image} alt={artist.name} loading="lazy" />
                        </div>
                        <div className="team-dashboard-artist-info">
                          <h3>{artist.name}</h3>
                          <a href={artist.link} target="_blank" rel="noopener noreferrer">
                            Open link
                          </a>
                          {artist.spotifyListeners && (
                            <div className="team-dashboard-artist-stats">
                              <span className="team-dashboard-stat spotify">
                                <i className="spotify-icon"></i>
                                {formatNumber(artist.spotifyListeners)} Listeners
                              </span>
                            </div>
                          )}
                          {artist.instagramFollowers && (
                            <div className="team-dashboard-artist-stats">
                              <span className="team-dashboard-stat instagram">
                                <i className="instagram-icon"></i>
                                {formatNumber(artist.instagramFollowers)} Followers
                              </span>
                            </div>
                          )}
                          <div className="team-dashboard-artist-actions">
                            <button 
                              onClick={() => handleEdit(artist)}
                              className="team-dashboard-edit-btn"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDelete(artist)}
                              className="team-dashboard-delete-btn"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="team-dashboard-no-artists">No artists found. Add your first artist using the form.</div>
                )}
              </div>
            </div>
          ) : activeTab === 'subscribers' ? (
            <NewsletterSubscribers />
          ) : (
            <ApplicationsList />
          )}
        </>
      )}
    </div>
  );
}

export default TeamDashboard; 